import type { Comparator, Metric } from '../../src/engine/schema.js';

/**
 * Independent metrics, never a composite score.
 *
 * The design rule from the outset: many separately defensible numbers that a
 * reader can check one at a time, rather than one index whose weighting is the
 * only thing worth arguing about.
 *
 * v2 removed the delay scenario, and with it the `deferred_by_delay` metric and
 * the `clamp(t - years_delayed + 1, 0, 1)` gate that used to zero a payment
 * stream until the delay had run. Payment streams now start in year one under
 * `built` and are zero under `not_built`.
 */
export const metrics: Metric[] = [
  // ================================================================ economic

  {
    id: 'net_local_tax',
    category: 'economic',
    label: 'Net new local property tax over the agreement term',
    plainLabel: 'New property taxes, after the discount',
    question: 'What does the town actually collect, once the tax break is subtracted?',
    unit: 'usd',
    polarity: 'more-is-better',
    expr: 'gross_new_taxes_28yr - abatement_total_28yr',
    series: {
      horizonYearsInput: 'agreement_term_years',
      perYearExpr: 'annual_net_local_tax',
    },
    methodology:
      'The city’s projected gross new property taxes over the 28-year agreement, less the abatement it granted. The year-by-year line divides that evenly across the term, because the city has not published the annual schedule.',
    caveats: [
      'Land and buildings only. Minnesota exempts business personal property for every industry, so the servers, storage and networking equipment are outside the property tax base entirely. A comparison with Loudoun County, Virginia, which taxes equipment and collects more from it than from real property, is not a valid analogue.',
      'This does not net out the cost of services the project consumes, nor the state sales tax exemption, which is a cost borne by a different government than the one collecting the property tax.',
      'Assessed value is set each 2 January, so there is a structural lag of roughly two years between construction spending and tax receipts that the flat annual profile does not capture.',
    ],
    comparatorIds: [
      'city_general_fund_comp',
      'teacher_years_comp',
      'fire_dept_years',
      'sheriff_years',
    ],
    displayAs: 'both',
    status: 'live',
  },
  {
    id: 'bill_savings_monthly',
    category: 'economic',
    label: 'Reduction in the average residential electricity bill',
    plainLabel: 'Taken off a monthly power bill',
    question: 'How much could this take off my electricity bill?',
    unit: 'usd_per_customer_month',
    polarity: 'more-is-better',
    expr:
      'xcel_customer_benefit_total / benefit_term_years * residential_revenue_share / nsp_mn_residential_customers / months_per_year',
    methodology:
      'The utility’s stated total benefit is a saving to customers, so this is money off a bill rather than added to it. That total is spread evenly over an assumed term, apportioned to the residential class on the residential share of retail revenue, then divided across residential customers and across the months of a year. Every step is one arithmetic operation on a figure you can look up.',
    caveats: [
      'The $1.1 billion is the company’s own estimate, made in an earnings release rather than in testimony. The model that produces it was filed as a trade secret, so no member of the public can check it, and there is no true-up requiring the estimate to be reconciled against what actually happens.',
      'The agreement it depends on has not been approved. The Commission is expected to decide in early 2027. If it is rejected or reduced, this figure goes with it.',
      'The disclosure states no time period. Fifteen years is the tariff’s minimum contract length, not a statement by the company, and the figure moves roughly in proportion as you change it.',
      'The residential allocator used by the utility is filed non-public. This uses the residential share of retail revenue as a proxy, and the slider spans the range between that and an energy-based allocation.',
      'The direction is not guaranteed. Large load lowers other customers’ rates only where the revenue it brings exceeds the cost of serving it. The Attorney General’s office argued in the tariff docket that the test does not require these customers to contribute to existing fixed costs as others do, and that the costs will land in riders where they cannot be tracked.',
      'Set against the same regulator’s decision in the same period to raise rates by roughly $211 million over two years, this is small. It is a real effect at the edge of a bill, not a change anyone would notice.',
    ],
    // Three views of one figure. Never add them together.
    overlapsWith: ['bill_savings_percent', 'system_price_effect'],
    displayAs: 'both',
    status: 'live',
  },
  {
    id: 'bill_savings_percent',
    category: 'economic',
    label: 'Reduction in the average residential bill, as a share of the bill',
    plainLabel: 'How much smaller a monthly bill would be',
    question: 'How much smaller is that bill, against what a household already pays?',
    unit: 'percent',
    polarity: 'more-is-better',
    expr:
      'xcel_customer_benefit_total / benefit_term_years * residential_revenue_share / nsp_mn_residential_customers / months_per_year / avg_residential_monthly_bill',
    methodology:
      'The monthly reduction divided by the average residential monthly bill, both for Xcel’s Minnesota customers.',
    caveats: [
      'Carries every limitation of the underlying bill figure: a company estimate, an unapproved agreement, a trade-secret model and an assumed term.',
      'The denominator is an annual average bill, so the percentage would look smaller against a January bill and larger against an April one.',
    ],
    overlapsWith: ['bill_savings_monthly', 'system_price_effect'],
    displayAs: 'value',
    status: 'live',
  },
  {
    id: 'system_price_effect',
    category: 'economic',
    label: 'Reduction in the price of power across all Minnesota retail sales',
    plainLabel: 'Taken off the price of power',
    question: 'Spread across every unit of power sold, how much comes off the price?',
    unit: 'usd_per_mwh',
    polarity: 'more-is-better',
    expr: 'xcel_customer_benefit_total / benefit_term_years / nsp_mn_total_sales',
    methodology:
      'The annualised benefit divided by total Minnesota retail sales for all classes. This is the system-wide version of the same figure, useful because it can be compared directly against a wholesale or retail price per megawatt-hour.',
    caveats: [
      'The data center’s own consumption is excluded from the denominator on purpose. The benefit accrues to customers other than the data center, so adding its load would understate the per-unit effect.',
      'Sales are held at their 2024 level. Real load grows, and other large customers may arrive, either of which changes the denominator.',
      'Carries every limitation of the underlying benefit estimate.',
    ],
    overlapsWith: ['bill_savings_monthly', 'bill_savings_percent'],
    displayAs: 'value',
    status: 'live',
  },
  {
    id: 'construction_apprenticeships',
    category: 'economic',
    label: 'Registered apprenticeship opportunity, Phase 1',
    plainLabel: 'Apprentices who get a year of training',
    question: 'How many apprentices can bank a full year of required hours on this build?',
    unit: 'apprentice_years',
    polarity: 'more-is-better',
    expr:
      'construction_job_years_direct * craft_hours_per_job_year * apprentice_hour_share / ojt_hours_per_apprentice_year',
    methodology:
      'Direct construction job-years converted to craft hours, multiplied by the share of hours worked by registered apprentices, divided by the on-the-job training hours one apprentice needs in a year.',
    caveats: [
      'Apprentices are inside the construction job-year count, not additional to it. Adding the two together double counts.',
      'Ratios under Minn. Stat. § 178.036 are ceilings, not floors. Nothing requires a contractor to employ apprentices up to the maximum.',
      'This assumes neither full signatory participation nor local hire. Goodhue County alone cannot supply this workforce; people will travel from Rochester and the Twin Cities.',
      'Phase 1 only. Summing across a 15 to 20 year, nine-stage buildout would count the same apprentice repeatedly.',
    ],
    overlapsWith: ['construction_job_years'],
    displayAs: 'both',
    status: 'live',
  },
  {
    id: 'construction_job_years',
    category: 'economic',
    label: 'Direct construction employment, Phase 1',
    plainLabel: 'Construction work on Phase 1',
    question: 'How much construction work is this, and for how long?',
    unit: 'job_years',
    polarity: 'more-is-better',
    expr: 'construction_job_years_direct',
    methodology:
      'Direct construction employment from the city’s commissioned economic impact study, reported in job-years over a two-year build.',
    caveats: [
      'Job-years, not simultaneous jobs. Roughly 320 to 380 people on site in an average year, not 700.',
      'Construction employment is temporary by nature. That is exactly why the apprenticeship figure is the more durable measure: a credential travels to the next job.',
      'Indirect and induced effects are deliberately excluded. Data center construction is unusually leak-prone — turbines, switchgear, chillers and specialty trades are largely sourced outside the region — so standard regional multipliers would overstate local capture.',
    ],
    overlapsWith: ['construction_apprenticeships'],
    displayAs: 'both',
    status: 'live',
  },
  {
    id: 'permanent_jobs',
    category: 'economic',
    label: 'Permanent on-site jobs at full buildout',
    plainLabel: 'Ongoing jobs once it is running',
    question: 'How many people would still work there once it is built?',
    unit: 'count',
    polarity: 'more-is-better',
    expr: 'permanent_jobs_full_buildout',
    methodology:
      'The operator’s own figure for staffing at full buildout, across all nine stages. Construction employment and operating employment are different things and are never added together here.',
    caveats: [
      'Full buildout is nine stages over 15 to 20 years. This is the endpoint, not the number on day one: staffing starts at 38 at stage one and declines by about two per stage to a floor of 30, so the total accumulates slowly.',
      'It is the developer’s figure and no independent verification of it exists in the public record.',
      'It counts on-site roles only. It excludes contracted security, cleaning and grounds maintenance, which are real jobs but are not in the stated number.',
      'A data center is capital-intensive rather than labour-intensive. Judged as jobs per dollar invested this is a low ratio, and that is a fair criticism of the project type rather than of this project in particular.',
    ],
    displayAs: 'both',
    status: 'live',
  },

  // ================================================================ civic

  {
    id: 'school_teacher_years',
    category: 'civic',
    label: 'School district payment, in teacher-years',
    plainLabel: 'Teachers the school payment would cover',
    question: 'What does the school district payment actually buy?',
    unit: 'teacher_years',
    polarity: 'more-is-better',
    expr: 'panther_base_payment / (teacher_avg_salary * teacher_benefit_load)',
    series: {
      horizonYearsInput: 'agreement_term_years',
      perYearExpr:
        '(panther_base_payment + panther_escalator * t) / (teacher_avg_salary * teacher_benefit_load)',
    },
    methodology:
      'The first-year Panther Program payment divided by the total annual cost of one teacher — the district’s own average salary multiplied by an employer benefit load. The year-by-year line applies the $25,000 annual escalator.',
    caveats: [
      'An illustrative equivalent, not a guaranteed allocation. Nothing obliges the district to spend this on teachers.',
      'The divisor is total compensation including employer benefits, not base salary. Dividing by salary alone is the standard error and would inflate this figure by about 40%.',
      'This is a developer payment, negotiated separately from the tax mechanics. It is genuinely new money to the district — unlike most property-tax growth, which under Minnesota’s equalisation formulas shifts cost from the state onto local taxpayers rather than raising district revenue.',
      'Stated in nominal dollars. If teacher costs rise at roughly the same rate as the escalator, the real figure stays near five to six teachers throughout rather than growing.',
    ],
    displayAs: 'both',
    status: 'live',
  },
  {
    id: 'community_fund_share_of_city',
    category: 'civic',
    label: 'Community Betterment Fund as a share of the city budget',
    plainLabel: 'How much the community fund is worth to the town',
    question: 'Set against what the city already spends, how big is this?',
    unit: 'percent',
    polarity: 'more-is-better',
    expr: 'betterment_annual_min / city_general_fund',
    methodology:
      'The minimum first-year Community Betterment Fund payment as a share of the City of Pine Island’s 2026 general fund.',
    caveats: [
      'The minimum payment. The agreement sets a floor of $50,000 a year rising by $5,000 annually, so later years are larger.',
      'A share of the general fund, which is the city’s operating core. Total all-funds spending, including water, sewer, debt service and capital, is larger.',
      'This is a comparison of scale, not a claim that the money is fungible with any particular service.',
    ],
    comparatorIds: [],
    displayAs: 'both',
    status: 'live',
  },

  // ================================================================ social

  {
    id: 'co2e_avoided',
    category: 'social',
    label: 'Carbon dioxide avoided by serving this load in Minnesota',
    plainLabel: 'Carbon saved by building here instead of a coal-heavy grid',
    question: 'Does it matter, for emissions, where this is built?',
    unit: 'short_tons_per_year',
    polarity: 'more-is-better',
    expr:
      'datacenter_load_mw * hours_per_year * load_factor * (alt_grid_co2e - serving_grid_co2e) * tons_per_lb',
    methodology:
      'Annual energy — load times hours in a year times load factor — multiplied by the difference between the Missouri and Minnesota grid carbon intensities, converted to short tons. Missouri is the comparison because Google announced a $15 billion data center there in May 2026.',
    caveats: [
      'This uses eGRID total output rates. eGRID also publishes non-baseload rates, intended as the proxy for incremental load, and on those the carbon gap between these two grids nearly disappears. The sulphur dioxide difference holds up on either basis; the carbon difference does not.',
      'Grid average intensities are an attributional measure. A rigorous "where should this load be served" question calls for long-run marginal rates, which move with scenario assumptions.',
      'The contracted 1,900 MW of new wind, solar and storage is not credited here. Published research finds that annual or volumetric clean-energy matching produces close to no long-run emissions reduction; only hourly matching does, and no hourly commitment for this facility is public.',
      'Both states are in MISO, and North Dakota, Iowa and Nebraska share Minnesota’s eGRID subregion — which is why they cannot be used as the comparison at all.',
    ],
    displayAs: 'both',
    status: 'live',
  },
  {
    id: 'so2_avoided',
    category: 'social',
    label: 'Sulphur dioxide avoided by serving this load in Minnesota',
    plainLabel: 'Sulphur pollution avoided by building here',
    question: 'What about the pollution that affects breathing, not just the climate?',
    unit: 'short_tons_per_year',
    polarity: 'more-is-better',
    expr:
      'datacenter_load_mw * hours_per_year * load_factor * (alt_grid_so2 - serving_grid_so2) * tons_per_lb',
    methodology:
      'The same energy calculation applied to sulphur dioxide intensity. This is the more robust of the two emissions comparisons, because the gap survives on non-baseload rates as well as total output rates.',
    caveats: [
      'Sulphur dioxide is a precursor to fine particulate matter, but emissions are not health effects. Translating one into the other requires air quality modelling this tool does not do.',
      'The Minnesota figure is the MROW subregion rate, which is raised by North Dakota lignite. Minnesota’s own state rate is 0.187 lb/MWh, so this comparison understates how clean Minnesota is.',
      'This tool makes no claim about pollution from one state being transported to another. North Dakota is not a Cross-State Air Pollution Rule state; Minnesota is.',
    ],
    displayAs: 'both',
    status: 'live',
  },
  {
    id: 'water_use',
    category: 'social',
    label: 'Permitted maximum water use',
    plainLabel: 'Water it is allowed to use',
    question: 'How much water is this, really?',
    unit: 'million_gallons_per_year',
    polarity: 'less-is-better',
    expr: 'water_permitted_mgy',
    methodology:
      '152,000 gallons a day at 365 days a year. Domestic use only — the facility is air-cooled and closed-loop, so there is no cooling tower draw.',
    caveats: [
      'A permitted ceiling at a site that has withdrawn nothing yet, not a measurement. Most of the comparisons below are measured actuals. Compare permitted against permitted, or actual against actual, but never across the two.',
      'The volume itself is not in dispute. Opponents cite the same figure. The dispute is whether the environmental review adequately studied aquifer and private well impacts, which this number does not address.',
    ],
    comparatorIds: [
      'golf_pine_island',
      'golf_median',
      'city_water',
      'irrigated_acres',
      'ski_snowmaking',
    ],
    displayAs: 'value',
    status: 'live',
  },
  {
    id: 'noise_margin',
    category: 'social',
    label: 'Night-time noise headroom against the standard that applies',
    plainLabel: 'How much quieter than the legal limit at night',
    question: 'Is the noise limit as comfortable as it sounds?',
    unit: 'dba',
    polarity: 'more-is-better',
    // Deliberately a small number. See cutsAgainst.
    expr: 'noise_night_l50_limit_dba - noise_night_estimate_dba',
    // Meaningless where the facility does not exist.
    appliesTo: ['built'],
    methodology:
      'The applicable Minnesota night-time residential standard less the project’s own estimated level at the nearest home.',
    caveats: [
      'The project’s public materials compare its 49 dBA estimate against the 55 dBA L10 standard, giving an apparent 6 dB of headroom. L10 is the level exceeded a tenth of the hour. A facility that runs continuously is an L50 source, and the L50 night-time standard is 50 dBA — so the real headroom is 1 dB.',
      'Rural night-time background runs 30 to 40 dBA. A continuous 49 dBA source therefore sits roughly 10 to 19 dB above the existing quiet, and a 10 dB rise is generally perceived as a doubling of loudness.',
      'A-weighting deliberately discounts low frequencies. The 100 to 500 Hz hum that drives most data center noise complaints contributes little to a dBA reading, so a facility can be fully compliant and still generate complaints.',
      'This is an estimate from a pre-construction study, not a measurement.',
    ],
    displayAs: 'value',
    status: 'live',
    cutsAgainst: true,
  },
  {
    id: 'capacity_coverage',
    category: 'social',
    label: 'Share of the load’s capacity need covered by contracted new resources',
    plainLabel: 'How much of its own power it brings',
    question: 'Does this project pay for the grid capacity it needs?',
    unit: 'percent',
    polarity: 'more-is-better',
    expr: 'accredited_capacity_mw / datacenter_load_mw',
    methodology:
      'Accredited summer capacity of the contracted portfolio divided by the facility’s load. Accreditation, not nameplate: MISO credits wind, solar and storage at a fraction of their rating based on availability when the system is tightest.',
    caveats: [
      '"Capacity neutral" is not a defensible description. The 1,900 MW headline is nameplate; under the accreditation method that applies when these resources energise, it covers roughly half the load’s accredited requirement in the binding summer season.',
      'The single largest uncertainty is the 100-hour iron-air storage. MISO has published no accreditation value for storage longer than four hours, and its own indicative results state they assume 4-hour batteries.',
      'Capacity is not the only channel. Transmission costs allocated across the whole MISO footprint on load-ratio share rise with a large Minnesota load regardless of what generation is contracted alongside it.',
      'This measures a contractual commitment, not a delivered outcome. The service agreement remains pending before the Minnesota Public Utilities Commission.',
    ],
    displayAs: 'value',
    status: 'live',
  },
];

/**
 * Comparators convert a metric into familiar units. The divisor is always an
 * Input with its own citation, so "that equals 62 teachers" is never an
 * unsourced number hiding inside a sentence.
 */
export const comparators: Comparator[] = [
  {
    id: 'golf_pine_island',
    plainLabel: 'the golf course in Pine Island, at its permitted maximum',
    unitCostInputId: 'golf_pine_island_permitted_mgy',
    verb: 'times',
    appliesToUnit: 'million_gallons_per_year',
    basis: 'permitted-maximum',
    note: 'Permitted against permitted — the only fair way to read this one. The course actually withdrew 9.4 million gallons in 2024.',
  },
  {
    id: 'golf_median',
    plainLabel: 'a typical Minnesota golf course, at its permitted maximum',
    unitCostInputId: 'golf_median_permitted_mgy',
    verb: 'times',
    appliesToUnit: 'million_gallons_per_year',
    basis: 'permitted-maximum',
    note: 'Median of 402 active golf irrigation permits statewide.',
  },
  {
    id: 'city_water',
    plainLabel: 'what the entire city of Pine Island actually pumped in 2024',
    unitCostInputId: 'city_water_actual_mgy',
    verb: 'times',
    appliesToUnit: 'million_gallons_per_year',
    basis: 'measured-actual',
    note: 'A permitted ceiling against a measured actual. Read with care.',
  },
  {
    id: 'ski_snowmaking',
    plainLabel: 'snowmaking at Welch Village, actual 2024',
    unitCostInputId: 'ski_snowmaking_actual_mgy',
    verb: 'times',
    appliesToUnit: 'million_gallons_per_year',
    basis: 'measured-actual',
    note: 'The closest single match in Goodhue County.',
  },
  {
    id: 'irrigated_acres',
    plainLabel: 'acres of cropland irrigated through a dry season',
    unitCostInputId: 'irrigation_mgy_per_acre',
    verb: 'acres',
    appliesToUnit: 'million_gallons_per_year',
    basis: 'measured-actual',
    note: 'Probably the most locally legible comparison in a farming county.',
  },
  {
    id: 'city_general_fund_comp',
    plainLabel: 'the City of Pine Island’s entire annual general fund',
    unitCostInputId: 'city_general_fund',
    verb: 'times',
    appliesToUnit: 'usd',
    basis: 'budgeted',
  },
  {
    id: 'fire_dept_years',
    plainLabel: 'years of running Pine Island Fire & Rescue',
    unitCostInputId: 'city_fire_budget',
    verb: 'years',
    appliesToUnit: 'usd',
    basis: 'budgeted',
    note:
      'The department’s 2026 operating budget. It is all-volunteer and covers about 130 square miles. This is the cost of running it, not the cost of building a station.',
  },
  {
    id: 'sheriff_years',
    plainLabel: 'years of the city’s policing contract',
    unitCostInputId: 'city_police_budget',
    verb: 'years',
    appliesToUnit: 'usd',
    basis: 'budgeted',
    note:
      'Pine Island contracts policing from the Goodhue County Sheriff at 126 hours a week. The 2026 cost rose 12% on the hourly rate alone.',
  },
  {
    id: 'teacher_years_comp',
    plainLabel: 'years of a teacher’s salary and benefits',
    // Not a single input: a teacher-year costs a salary times a benefit load,
    // and collapsing that into one figure would hide the assumption inside it.
    unitCostExpr: 'teacher_avg_salary * teacher_benefit_load',
    verb: 'teacher-years',
    appliesToUnit: 'usd',
    basis: 'budgeted',
    note:
      'District average salary loaded for pension, payroll tax and health contributions. It is what a teacher costs a district, not what a teacher is paid.',
  },
];
